// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { PartyCredentialRequirement } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import {
  RegistrarService,
  RegistrarService_CreateInstrumentConfiguration_Result,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import { ContractId } from '@daml/types';
import { useLedgerApiClient } from '../../../../contexts/LedgerApiContext';

const useArchiveAndCreateInstrumentConfiguration: () => UseMutationResult<
  RegistrarService_CreateInstrumentConfiguration_Result,
  string,
  {
    registrarServiceCid: ContractId<RegistrarService>;
    instrumentConfigurationCid: ContractId<InstrumentConfiguration>;
    instrumentId: string;
    additionalIdentifiers: InstrumentIdentifier[];
    issuerRequirements: PartyCredentialRequirement[];
    holderRequirements: PartyCredentialRequirement[];
  }
> = () => {
  const ledgerApi = useLedgerApiClient();

  return useMutation({
    mutationKey: ['archiveAndCreateInstrumentConfiguration', ledgerApi],
    mutationFn: async ({
      registrarServiceCid,
      instrumentConfigurationCid,
      instrumentId,
      additionalIdentifiers,
      issuerRequirements,
      holderRequirements,
    }) => {
      return ledgerApi.archiveAndCreateRegistryInstrumentConfiguration(
        registrarServiceCid,
        instrumentConfigurationCid,
        instrumentId,
        additionalIdentifiers,
        issuerRequirements,
        holderRequirements,
      );
    },
  });
};

export default useArchiveAndCreateInstrumentConfiguration;
